import axios from "axios";
import ThemeMaker from "../../components/thememaker";
import { config } from "../../utils/config";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import styles from "./UserDetails.module.css";

import {
    Form,
    Input,
    Row,
    Col,
    Button,
    Upload,
    Avatar,
    Card
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';

const PHONE_REGEX = /^[6-9]\d{9}$/;
const PAN_REGEX = /^[A-Z]{5}\d{4}[A-Z]$/;
const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const IMAGE_MIME_REGEX = /^image\//;
const IMAGE_EXTENSION_REGEX = /\.(?:jpe?g|png|gif|webp|bmp|svg)$/i;
const EMPTY_OR_PLACEHOLDER = (value) => !value || value === "N/A";

// Skip validation for empty / "N/A" placeholder values coming from the API
const phoneRules = [
    {
        validator: (_, value) =>
            EMPTY_OR_PLACEHOLDER(value) || PHONE_REGEX.test(value)
                ? Promise.resolve()
                : Promise.reject(new Error("Enter a valid 10-digit phone number")),
    },
];
const emailRules = [
    {
        validator: (_, value) =>
            !value || value === "N/A" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
                ? Promise.resolve()
                : Promise.reject(new Error("Enter a valid email address")),
    },
];
const panRules = [
    {
        validator: (_, value) =>
            EMPTY_OR_PLACEHOLDER(value) || PAN_REGEX.test(value)
                ? Promise.resolve()
                : Promise.reject(new Error("Enter a valid PAN number")),
    },
];
const gstRules = [
    {
        validator: (_, value) =>
            EMPTY_OR_PLACEHOLDER(value) || GST_REGEX.test(value)
                ? Promise.resolve()
                : Promise.reject(new Error("Enter a valid GST number")),
    },
];

const UserDetails = () => {
    const { apiEndpoint, uploadUrl } = config;
    const [form] = Form.useForm();
    const [userData, setUserData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [previewFiles, setPreviewFiles] = useState({ logo: [], pan_image: [], gst_image: [] });

    const buildFileList = (url) => {
        if (!url || url === "N/A") return [];
        return [{ uid: "-1", name: "current-file", status: "done", url }];
    };

    const getUserData = async () => {
        try {
            const response = await axios({
                url: `${apiEndpoint}/get-user-data`,
                method: "POST",
                headers: {
                    "content-type": "application/json",
                    "X-Custom-Authorization": `Bearer ${sessionStorage.getItem("us_st_d")}`
                }
            });
            const data = response?.data?.data;
            const formattedData = Object.keys(data).reduce((acc, key) => {
                acc[key] = data[key] === null || data[key] === "" ? "N/A" : data[key];
                return acc;
            }, {});
            setUserData(formattedData);
            form.setFieldsValue(formattedData);
            setPreviewFiles({
                logo: buildFileList(formattedData.logo),
                pan_image: buildFileList(formattedData.pan_image),
                gst_image: buildFileList(formattedData.gst_image),
            });
        } catch (error) {
            console.log(error);
        }
    };

    const updateUserData = async (values) => {
        setIsLoading(true);
        try {
            const response = await axios({
                url: `${apiEndpoint}/update-user-data`,
                method: "POST",
                headers: {
                    "content-type": "application/json",
                    "X-Custom-Authorization": `Bearer ${sessionStorage.getItem("us_st_d")}`
                },
                data: values
            });
            toast.success(response?.data?.message);
            getUserData();
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            toast.error("Update Failed");
        }
    };

    const handleUpload = (info, field) => {
        const newList = info.fileList.slice(-1).map((f) =>
            f.response?.urls?.[0] ? { ...f, url: f.response.urls[0], status: "done" } : f
        );
        setPreviewFiles((prev) => ({ ...prev, [field]: newList }));

        if (info.file.status === "done") {
            const url = info.file.response?.urls?.[0];
            form.setFieldsValue({
                [field]: url
            });
        } else if (info.file.status === "removed") {
            form.setFieldsValue({ [field]: undefined });
        }
    };

    const beforeUpload = (file, field) => {
        const isValidImage = IMAGE_MIME_REGEX.test(file.type || "") && IMAGE_EXTENSION_REGEX.test(file.name || "");

        if (!isValidImage) {
            form.setFields([{ name: field, errors: ["Please select a valid image document (JPG, PNG, GIF, WEBP, BMP, or SVG)."] }]);
            return Upload.LIST_IGNORE;
        }

        form.setFields([{ name: field, errors: [] }]);
        return true;
    };

    // Upload's onChange event isn't a plain value, so without this Form.Item's
    // default getValueFromEvent stores the raw event and overwrites the url
    // that handleUpload just set — the field ends up empty in the submit payload.
    const normUploadValue = (field) => (e) => {
        if (Array.isArray(e)) return e;
        if (e?.file?.status === "done") {
            return e.file.response?.urls?.[0];
        }
        return form.getFieldValue(field);
    };

    useEffect(() => {
        getUserData();
    }, []);

    return (
        <ThemeMaker>
            <div className={styles.pageWrap}>
                <div className={styles.hero}>
                    <div className={styles.avatarRing}>
                        <Avatar size={80} src={previewFiles.logo?.[0]?.url} />
                    </div>
                    <div className={styles.heroText}>
                        <div className={styles.companyName}>{userData?.company_name}</div>
                        <div className={styles.heroMetaRow}>
                            <div className={styles.sellerIdChip}>
                                <EmailOutlinedIcon style={{ fontSize: 15 }} />
                                {userData?.user_email}
                            </div>
                            <div className={styles.sellerIdChip}>
                                <PhoneOutlinedIcon style={{ fontSize: 15 }} />
                                {userData?.user_mobile}
                            </div>
                        </div>
                    </div>
                </div>

                <Card className={styles.formCard}>
                <div className={styles.sectionTitle}>Account Information</div>
                <Form layout="vertical" form={form} onFinish={updateUserData}>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="Company Name" name="company_name" style={{ marginBottom: 10 }}>
                                <Input className="p-2" />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item label="Contact Number" name="contact_no" style={{ marginBottom: 10 }} rules={phoneRules}>
                                <Input className="p-2" inputMode="numeric" maxLength={10} />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item label="User Name" name="user_name" style={{ marginBottom: 10 }}>
                                <Input className="p-2" />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item label="User Email" name="user_email" style={{ marginBottom: 10 }} rules={emailRules}>
                                <Input className="p-2" />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item label="Features Enabled" name="features_enabled">
                                <Input
                                    disabled
                                    className="p-2"
                                    value={form.getFieldValue("features_enabled") || "N/A"}
                                />
                            </Form.Item>
                        </Col>

                        {/* <Col span={8}>
                            <Form.Item label="Display Email" name="user_display_email" style={{ marginBottom: 10 }}>
                                <Input className="p-2" />
                            </Form.Item>
                        </Col> */}

                        <Col span={8}>
                            <Form.Item label="User Mobile" name="user_mobile" style={{ marginBottom: 10 }} rules={phoneRules}>
                                <Input className="p-2" inputMode="numeric" maxLength={10} />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item label="PAN Number" name="pan_number" style={{ marginBottom: 10 }} rules={panRules}>
                                <Input className="p-2" maxLength={10} />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item label="GST Number" name="gst_number" style={{ marginBottom: 10 }} rules={gstRules}>
                                <Input className="p-2" maxLength={15} />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item label="Short Description" name="short_description" style={{ marginBottom: 10 }}>
                                <Input className="p-2" />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item label="Seller Source" name="seller_source" style={{ marginBottom: 10 }}>
                                <Input className="p-2" />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item label="Status" name="status" style={{ marginBottom: 10 }}>
                                <Input className="p-2" />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item label="Approve Status" name="approve_status" style={{ marginBottom: 10 }}>
                                <Input className="p-2" />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <div className={styles.sectionTitle}>Documents & Verification</div>
                        </Col>

                        <Col span={8}>
                            <Form.Item label="Company Logo" name="logo" getValueFromEvent={normUploadValue("logo")} style={{ marginBottom: 10 }}>
                                <Upload
                                    name="file"
                                    listType="picture-card"
                                    maxCount={1}
                                    fileList={previewFiles.logo}
                                    action={`${uploadUrl}/upload`}
                                    accept="image/*"
                                    beforeUpload={(file) => beforeUpload(file, "logo")}
                                    headers={{
                                        "X-Custom-Authorization": `Bearer ${sessionStorage.getItem("us_st_d")}`
                                    }}
                                    onChange={(info) => handleUpload(info, "logo")}
                                >
                                    {previewFiles.logo.length === 0 && (
                                        <div>
                                            <UploadOutlined />
                                            <div style={{ marginTop: 8, fontSize: 12 }}>Upload</div>
                                        </div>
                                    )}
                                </Upload>
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item label="PAN Image" name="pan_image" getValueFromEvent={normUploadValue("pan_image")} style={{ marginBottom: 10 }}>
                                <Upload
                                    name="file"
                                    listType="picture-card"
                                    maxCount={1}
                                    fileList={previewFiles.pan_image}
                                    action={`${uploadUrl}/upload`}
                                    accept="image/*"
                                    beforeUpload={(file) => beforeUpload(file, "pan_image")}
                                    headers={{
                                        "X-Custom-Authorization": `Bearer ${sessionStorage.getItem("us_st_d")}`
                                    }}
                                    onChange={(info) => handleUpload(info, "pan_image")}
                                >
                                    {previewFiles.pan_image.length === 0 && (
                                        <div>
                                            <UploadOutlined />
                                            <div style={{ marginTop: 8, fontSize: 12 }}>Upload</div>
                                        </div>
                                    )}
                                </Upload>
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item label="GST Image" name="gst_image" getValueFromEvent={normUploadValue("gst_image")} style={{ marginBottom: 10 }}>
                                <Upload
                                    name="file"
                                    listType="picture-card"
                                    maxCount={1}
                                    fileList={previewFiles.gst_image}
                                    action={`${uploadUrl}/upload`}
                                    accept="image/*"
                                    beforeUpload={(file) => beforeUpload(file, "gst_image")}
                                    headers={{
                                        "X-Custom-Authorization": `Bearer ${sessionStorage.getItem("us_st_d")}`
                                    }}
                                    onChange={(info) => handleUpload(info, "gst_image")}
                                >
                                    {previewFiles.gst_image.length === 0 && (
                                        <div>
                                            <UploadOutlined />
                                            <div style={{ marginTop: 8, fontSize: 12 }}>Upload</div>
                                        </div>
                                    )}
                                </Upload>
                            </Form.Item>
                        </Col>
                        
                        <Col span={24}>
                            <Form.Item label="Description" name="description" >
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Button type="primary" htmlType="submit" className={styles.submitBtn} style={{ borderRadius: "10px" }}>
                        {isLoading ? "Loading..." : " Update Seller"}
                    </Button>
                </Form>
                </Card>
            </div>
        </ThemeMaker>
    );
};

export default UserDetails;
